import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/authContext";
import { updateProfile, getProfile } from "../../services/api";
import { useNavigate } from "react-router-dom";
import "./../styles/UserProfile.css";
import { FaUser } from 'react-icons/fa';

const UserProfile = () => {
  const {
    admin,
    user,
    login,
    logout,
    checkAuth,
    shouldRedirectToLogin,
    resetRedirectFlag,
  } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [formData, setFormData] = useState({});
  const [profileData, setProfileData] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const navigate = useNavigate();

  // Определяем текущего пользователя
  const currentUser = admin || user;
  const isAdmin = !!admin;

  // ОТЛАДКА: Логируем состояния
  console.log("isEditing:", isEditing);
  console.log("currentUser:", currentUser);
  console.log("formData:", formData);

  // Обрабатываем редирект на login
  useEffect(() => {
    if (shouldRedirectToLogin) {
      resetRedirectFlag();
      navigate("/login");
      return;
    }
  }, [shouldRedirectToLogin, resetRedirectFlag, navigate]);

  // Проверяем авторизацию при загрузке компонента
  useEffect(() => {
    // Проверяем наличие токена в localStorage
    const token = localStorage.getItem("token");

    if (!token) {
      // Если токена нет, перекидываем на login
      navigate("/login");
      return;
    }

    // Проверяем валидность токена
    const isValidToken = checkAuth();
    if (!isValidToken) {
      navigate("/login");
      return;
    }

    // Если токен валиден, загружаем профиль
    loadProfile();
  }, [navigate, checkAuth]);

  const loadProfile = async () => {
    try {
      setInitialLoading(true);
      setError("");
      const data = await getProfile();
      setProfileData(data);

      if (data.admin) {
        // Если получили данные администратора, обновляем контекст
        const token = localStorage.getItem("token");
        if (token && !admin) {
          login(token, {
            ...data.admin,
            role: "admin",
          });
        }

        setFormData({
          name: data.admin.name || "",
          password: "",
        });
      } else if (data.user) {
        // Если получили данные пользователя, обновляем контекст
        const token = localStorage.getItem("token");
        if (token && !user) {
          login(token, {
            ...data.user,
            role: "user",
          });
        }

        setFormData({
          lastName: data.user.lastName || "",
          firstName: data.user.firstName || "",
          middleName: data.user.middleName || "",
          gender: data.user.gender || "not_specified",
          birthYear: data.user.birthYear ? String(data.user.birthYear) : "",
          nationality: data.user.nationality || "",
          address: data.user.address || "",
          education: data.user.education || "secondary",
          socialStatus: data.user.socialStatus || "other",
          workplace: data.user.workplace || "",
          academicDegree: data.user.academicDegree || "",
          studyPlace: data.user.studyPlace || "",
          password: "",
          avatar: null
        });
      }
    } catch (err) {
      console.error("Ошибка загрузки профиля:", err);

      // Если токен истек или невалиден, очищаем данные и перекидываем на login
      if (err.response?.status === 401) {
        logout();
        return;
      }

      setError("Ошибка загрузки данных профиля. Попробуйте обновить страницу.");

      // Используем данные из localStorage как fallback
      const role = localStorage.getItem("role");
      if (role === "admin") {
        setFormData({
          name: localStorage.getItem("name") || "",
          password: "",
        });
      } else {
        setFormData({
          lastName: localStorage.getItem("lastName") || "",
          firstName: localStorage.getItem("firstName") || "",
          middleName: localStorage.getItem("middleName") || "",
          gender: "not_specified",
          birthYear: "",
          nationality: "",
          address: "",
          education: "secondary",
          socialStatus: "other",
          workplace: "",
          academicDegree: "",
          studyPlace: "",
          password: "",
          avatar: null
        });
      }
    } finally {
      setInitialLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Очищаем ошибку валидации для поля при изменении
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    const currentRole = localStorage.getItem("role");
    const isAdminUser = currentRole === "admin";

    if (isAdminUser) {
      if (!formData.name?.trim()) {
        errors.name = "Имя обязательно для заполнения";
      }
    } else {
      if (!formData.lastName?.trim()) {
        errors.lastName = "Фамилия обязательна для заполнения";
      }
      if (!formData.firstName?.trim()) {
        errors.firstName = "Имя обязательно для заполнения";
      }
      if (!formData.address?.trim()) {
        errors.address = "Адрес обязателен для заполнения";
      }
      if (!formData.birthYear?.trim()) {
        errors.birthYear = "Год рождения обязателен для заполнения";
      } else {
        const year = parseInt(formData.birthYear);
        if (isNaN(year) || year < 1900 || year > new Date().getFullYear()) {
          errors.birthYear = "Некорректный год рождения";
        }
      }
    }

    if (
      formData.password &&
      formData.password.length > 0 &&
      formData.password.length < 6
    ) {
      errors.password = "Пароль должен содержать минимум 6 символов";
    }

    if (formData.avatar && formData.avatar.size > 15 * 1024 * 1024) {
      errors.avatar = "Файл слишком большой. Максимум 15MB"
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    console.log("handleSubmit вызван, isEditing:", isEditing);

    if (!validateForm()) {
      setError("Пожалуйста, исправьте ошибки в форме");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    const form = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      if (key !== "avatar") {

        if (key === "password" && (!value || value.trim() === "")) {
          return;
        }
        form.append(key, value);
      }
    })

    if (formData.avatar) {
      form.append("avatar", formData.avatar)
    }
    try {
      const updatedData = await updateProfile(form);

      // Обновляем данные в контексте
      const token = localStorage.getItem("token");
      if (updatedData.admin) {
        login(token, {
          ...updatedData.admin,
          role: "admin",
        });
      } else if (updatedData.user) {
        login(token, {
          ...updatedData.user,
          role: "user",
        });
      }

      setSuccess("Профиль успешно обновлен!");

      // НЕ ВЫХОДИМ ИЗ РЕЖИМА РЕДАКТИРОВАНИЯ АВТОМАТИЧЕСКИ
      // setIsEditing(false); // Убрали эту строку

      // Очищаем поле пароля
      setFormData((prev) => ({
        ...prev,
        password: "",
      }));

      // Перезагружаем данные профиля
      await loadProfile();

      // Убрали автоматическое скрытие сообщения
      // setTimeout(() => {
      //   setSuccess('');
      // }, 3000);
    } catch (err) {
      console.error("Ошибка обновления профиля:", err);
      if (err.response?.status === 401) {
        logout();
        return;
      }
      setError(err.response?.data?.message || "Ошибка при обновлении профиля");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError("");
    setSuccess("");
    setValidationErrors({});
    loadProfile(); // Сбрасываем изменения
  };

  const handleEditClick = () => {
    console.log("Включаем режим редактирования");
    setError("");
    setSuccess("");
    setValidationErrors({});
    setIsEditing(true);
  };

  const handleDoneClick = () => {
    console.log("Выходим из режима редактирования");
    setIsEditing(false);
    setSuccess("");
    setError("");
    setValidationErrors({});
  };

  // Обработчик выхода из системы
  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    // Удаляем все данные из localStorage
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("email");
    localStorage.removeItem("libraryCard");
    localStorage.removeItem("name");
    localStorage.removeItem("firstName");
    localStorage.removeItem("lastName");
    localStorage.removeItem("middleName");

    // Вызываем logout из контекста
    logout();

    // Перенаправляем на главную страницу
    navigate("/");

    setShowLogoutConfirm(false);
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const renderReadOnlyField = (label, value, icon = "") => (
    <div className="profile-field">
      <label>
        {icon && <span>{icon}</span>}
        {label}:
      </label>
      <span className="field-value">{value || "—"}</span>
    </div>
  );

  const renderEditableField = (
    label,
    name,
    type = "text",
    options = null,
    required = false,
    icon = ""
  ) => (
    <div className={`profile-field ${validationErrors[name] ? "error" : ""}`}>
      <label htmlFor={name}>
        {icon && <span>{icon}</span>}
        {label}:{required && <span className="required">*</span>}
      </label>
      {type === "select" ? (
        <select
          id={name}
          name={name}
          value={formData[name] || ""}
          onChange={handleInputChange}
          disabled={!isEditing}
          required={required}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : type === "textarea" ? (
        <textarea
          id={name}
          name={name}
          value={formData[name] || ""}
          onChange={handleInputChange}
          disabled={!isEditing}
          rows={3}
          required={required}
          placeholder={isEditing ? `Введите ${label.toLowerCase()}` : ""}
        />
      ) : (
        <input
          type={type}
          id={name}
          name={name}
          value={formData[name] || ""}
          onChange={handleInputChange}
          disabled={!isEditing}
          required={required}
          placeholder={isEditing ? `Введите ${label.toLowerCase()}` : ""}
          min={type === "number" && name === "birthYear" ? "1900" : undefined}
          max={
            type === "number" && name === "birthYear"
              ? new Date().getFullYear()
              : undefined
          }
        />
      )}
      {validationErrors[name] && (
        <small className="validation-error">{validationErrors[name]}</small>
      )}
    </div>
  );

  // Показываем загрузку при первоначальной загрузке
  if (initialLoading) {
    return (
      <div className="profile-container">
        <div className="profile-card">
          <div className="loading-state">
            <h2>Загрузка профиля...</h2>
            <div className="loading-spinner">⏳</div>
          </div>
        </div>
      </div>
    );
  }

  // Определяем роль из localStorage если контекст еще не загружен
  const currentRole = localStorage.getItem("role");
  const isAdminRole = currentRole === "admin" || isAdmin;

  return (
    <div className="profile-container">
      <div className="profile-card">
        <div className="profile-header">
          <div className="profile-title">

            <h1>Мой профиль</h1>
            <span className="profile-role">
              {isAdminRole ? "Администратор" : "Пользователь"}
            </span>
          </div>

          {/* КНОПКИ УПРАВЛЕНИЯ ВЫНЕСЕНЫ ЗА ПРЕДЕЛЫ ФОРМЫ */}
          <div className="profile-actions">
            {!isEditing ? (
              <div className="action-buttons">
                <button
                  type="button"
                  onClick={handleEditClick}
                  className="edit-button"
                >
                  <span>✏️</span>
                  Редактировать
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="logout-button"
                >
                  <span>🚪</span>
                  Выйти
                </button>
              </div>
            ) : (
              <div className="edit-actions">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="save-button"
                >
                  {loading ? (
                    <>
                      <span className="loading-spinner">⏳</span>
                      Сохранение...
                    </>
                  ) : (
                    <>
                      <span>💾</span>
                      Сохранить
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleDoneClick}
                  disabled={loading}
                  className="done-button"
                >
                  <span>✅</span>
                  Готово
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={loading}
                  className="cancel-button"
                >
                  <span>❌</span>
                  Отмена
                </button>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="error-message">
            <span>⚠️</span>
            {error}
          </div>
        )}

        {success && (
          <div className="success-message">
            <span>✅</span>
            {success}
          </div>
        )}

        {/* ФОРМА БЕЗ КНОПОК УПРАВЛЕНИЯ */}
        <div className="profile-form">
          <div className="profile-sections">
            {/* Основная информация */}
            <div className="profile-section">
              <h2>
                <span>📋</span> Основная информация
              </h2>

              {isAdminRole ? (
                <>
                  {renderEditableField("Имя", "name", "text", null, true, <FaUser />)}
                </>
              ) : (
                <>
                  {renderEditableField(
                    "Фамилия",
                    "lastName",
                    "text",
                    null,
                    true,
                    <FaUser />
                  )}
                  {renderEditableField(
                    "Имя",
                    "firstName",
                    "text",
                    null,
                    true,
                    <FaUser />
                  )}
                  {renderEditableField(
                    "Отчество",
                    "middleName",
                    "text",
                    null,
                    false,
                    <FaUser />
                  )}
                  {renderEditableField(
                    "Пол",
                    "gender",
                    "select",
                    [
                      { value: "male", label: "Мужской" },
                      { value: "female", label: "Женский" },
                      { value: "other", label: "Другой" },
                      { value: "not_specified", label: "Не указан" },
                    ],
                    true,
                    "⚧"
                  )}
                  {renderEditableField(
                    "Год рождения",
                    "birthYear",
                    "number",
                    null,
                    true,
                    "📅"
                  )}
                  {renderEditableField(
                    "Национальность",
                    "nationality",
                    "text",
                    null,
                    false,
                    "🌍"
                  )}
                  {renderEditableField(
                    "Адрес",
                    "address",
                    "textarea",
                    null,
                    true,
                    "📍"
                  )}
                </>
              )}

              {/* Неизменяемые поля */}
              {renderReadOnlyField(
                "Email",
                profileData
                  ? isAdminRole
                    ? profileData.admin?.email
                    : profileData.user?.email
                  : localStorage.getItem("email"),
                "📧"
              )}
              {renderReadOnlyField(
                "Читательский билет",
                profileData
                  ? isAdminRole
                    ? profileData.admin?.libraryCard
                    : profileData.user?.libraryCard
                  : localStorage.getItem("libraryCard"),
                "🎫"
              )}
              {renderReadOnlyField(
                "Роль",
                isAdminRole ? "Администратор" : "Пользователь",
                "👑"
              )}
            </div>

            {/* Образование и работа (только для пользователей) */}
            {!isAdminRole && (
              <div className="profile-section">
                <h2>
                  <span>🎓</span> Образование и работа
                </h2>

                {renderEditableField(
                  "Образование",
                  "education",
                  "select",
                  [
                    { value: "elementary", label: "Начальное" },
                    { value: "secondary", label: "Среднее" },
                    { value: "vocational", label: "Профессиональное" },
                    { value: "higher", label: "Высшее" },
                    { value: "academic_degree", label: "Ученая степень" },
                  ],
                  true,
                  "📚"
                )}

                {renderEditableField(
                  "Социальный статус",
                  "socialStatus",
                  "select",
                  [
                    { value: "student", label: "Студент" },
                    { value: "employed", label: "Работающий" },
                    { value: "unemployed", label: "Безработный" },
                    { value: "pensioner", label: "Пенсионер" },
                    { value: "school_student", label: "Школьник" },
                    { value: "entrepreneur", label: "Предприниматель" },
                    { value: "civil_servant", label: "Госслужащий" },
                    { value: "other", label: "Другое" },
                  ],
                  true,
                  "💼"
                )}

                {renderEditableField(
                  "Место работы",
                  "workplace",
                  "text",
                  null,
                  false,
                  "🏢"
                )}
                {renderEditableField(
                  "Ученая степень",
                  "academicDegree",
                  "text",
                  null,
                  false,
                  "🎓"
                )}
                {renderEditableField(
                  "Место учебы",
                  "studyPlace",
                  "text",
                  null,
                  false,
                  "🏫"
                )}
              </div>
            )}

            {/* Безопасность */}
            <div className="profile-section">
              <h2>
                <span>🔐</span> Безопасность
              </h2>

              {isEditing ? (
                <div className="profile-field">
                  <label htmlFor="password">
                    <span>🔒</span>
                    Новый пароль:
                    <small className="field-hint">
                      Оставьте пустым, если не хотите менять пароль
                    </small>
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password || ""}
                    onChange={handleInputChange}
                    placeholder="Введите новый пароль"
                    minLength="6"
                  />
                  {validationErrors.password && (
                    <small className="validation-error">
                      {validationErrors.password}
                    </small>
                  )}
                </div>
              ) : (
                <div className="password-info">
                  <p>
                    <span>🔒</span>
                    Для изменения пароля используйте режим редактирования.
                  </p>
                  <small>
                    Последнее обновление:{" "}
                    {profileData
                      ? new Date(
                        isAdminRole
                          ? profileData.admin?.updatedAt
                          : profileData.user?.updatedAt
                      ).toLocaleDateString("ru-RU")
                      : "Неизвестно"}
                  </small>
                </div>
              )}
            </div>
            
            {!isAdminRole && (

              <div className="profile-section">
                <h2>
                  <span><FaUser /></span>Аватар
                </h2>
                {isEditing ? (
                  <div className="profile-field">
                    <label htmlFor="avatar">
                      <span><FaUser /></span>
                      Новый Аватар:
                    </label>
                    <input
                      type="file"
                      id="avatar"
                      name="avatar"
                      accept="image/*"
                      disabled={loading}
                      onChange={(e) => setFormData({ ...formData, avatar: e.target.files[0] })}
                    />
                    {validationErrors.avatar && (
                      <small className="validation-error">
                        {validationErrors.avatar}
                      </small>
                    )}
                  </div>
                ) : (
                  <div>
                    <input
                      type="file"
                      id="avatar"
                      name="avatar"
                      disabled={!isEditing}
                    />
                  </div>
                )}
              </div>
            )}


          </div>
        </div>
      </div>

      {/* Модальное окно подтверждения выхода */}
      {showLogoutConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Подтверждение выхода</h3>
            </div>
            <div className="modal-body">
              <p>Вы действительно хотите выйти из системы?</p>
              <p className="modal-warning">
                <span>⚠️</span>
                Все несохраненные изменения будут потеряны.
              </p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                onClick={confirmLogout}
                className="confirm-logout-button"
              >
                <span>🚪</span>
                Да, выйти
              </button>
              <button
                type="button"
                onClick={cancelLogout}
                className="cancel-logout-button"
              >
                <span>❌</span>
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
